<!--

  @component

  ### NavigationLoader

  A simple loader that is displayed when the user navigates between pages.
  The loader is displayed when the user navigates to a new page and is hidden when the page is loaded.
  It is only visible when navigating from already loaded page and not when the page is loaded for the first time.

-->

<script lang="ts">
  import { navigating } from '$app/stores';
  import { onMount } from 'svelte';

  let timer = 0;

  let display = false;

  let width = 0;

  /**
   * ✨ magic numbers ✨
   *
   * timer is incremented every 100ms, thus timer === 10 means 1s
   *
   * 0 - 5: 0%
   * 5 - 15: 20%
   * 15 - 40: 75%
   * 40+: 100%
   *
   * Because of the loading time being unknown, the timer does never go above 40 on its own.
   * 4 seconds is a reasonable time to wait for the page to load.
   * If the time is exceeded, the loader will be stuck at 75% indefinitely until the page is loaded.
   */

  onMount(() => {
    setInterval(() => {
      if (timer < 39) {
        timer++;
      }
    }, 100);
  });

  $: width = timer < 5 ? 0 : timer < 15 ? 20 : timer < 40 ? 75 : 100;

  $: if ($navigating) {
    display = true;
    timer = 0;
  } else if (display && !$navigating) {
    timer = 50;
    setTimeout(() => {
      display = false;
    }, 500);
  }
</script>

{#if display}
  <div class="navigation-loader">
    <span style:--width="{width}%" />
  </div>
{/if}

<style lang="scss">
  .navigation-loader {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10000;
    span {
      display: block;
      border: 1px solid $color-caramel;
      width: var(--width);
      transition: 400ms ease all;
    }
  }
</style>
